import { ArrowRight, Sparkles, Flame, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { formatGrowthDisplay, growthTierForOpportunity } from "../../lib/opportunityMarketRanking";
import { humanizeOpportunityTitle } from "../../lib/opportunityDisplay";

const HEAT_INLINE = {
  hot: {
    icon: <Flame className="h-3 w-3 shrink-0 text-rose-400" aria-hidden />,
    label: "Hot",
    text: "text-rose-300/90",
  },
  rising: {
    icon: <TrendingUp className="h-3 w-3 shrink-0 text-emerald-400" aria-hidden />,
    label: "Rising",
    text: "text-emerald-300/90",
  },
  stable: {
    icon: <Minus className="h-3 w-3 shrink-0 text-zinc-500" aria-hidden />,
    label: "Stable",
    text: "text-zinc-400",
  },
  cooling: {
    icon: <TrendingDown className="h-3 w-3 shrink-0 text-sky-400" aria-hidden />,
    label: "Cooling",
    text: "text-sky-300/90",
  },
};

function revenueMetricCompact(opp) {
  const verifiedMrr = Number(opp?.display_mrr_usd ?? opp?.market_avg_mrr_usd) || 0;
  const fmt = (v, currency = "€") => (v >= 1000 ? `${currency}${Math.round(v / 1000)}k` : `${currency}${Math.round(v)}`);

  if (verifiedMrr > 0) {
    return {
      value: fmt(verifiedMrr, "$"),
      label: "Peer MRR",
    };
  }

  const estMin = Number(opp?.estimated_mrr_min) || 0;
  const estMax = Number(opp?.estimated_mrr_max) || 0;

  let baseMin = estMin;
  let baseMax = estMax;

  if (!(baseMin > 0 && baseMax > 0)) return { value: "—", label: "MRR" };

  const startMin = Math.max(100, Math.round(baseMin * 0.18));
  const startMax = Math.max(startMin, Math.round(baseMax * 0.35));

  return {
    value: startMin === startMax ? fmt(startMin) : `${fmt(startMin)}-${fmt(startMax)}`,
    label: "Modeled MRR",
  };
}

/**
 * Compact opportunity card — same layout as the Opportunities dashboard grid.
 */
export default function OpportunityCompactCard({ opp, onCardClick, onWhyClick, dataTestId }) {
  const opportunityId = opp.opportunity_id || opp.id;
  const heatTier = growthTierForOpportunity(opp);
  const heat = HEAT_INLINE[heatTier] || HEAT_INLINE.stable;
  const growthStr = formatGrowthDisplay(opp);
  const growthTone =
    growthStr === "—"
      ? "text-zinc-500"
      : growthStr.trim().startsWith("-")
        ? "text-rose-400/90"
        : "text-emerald-400/95";
  const title = humanizeOpportunityTitle(opp.name);
  const revenueMetric = revenueMetricCompact(opp);

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onCardClick?.(opportunityId, opp);
        }
      }}
      className="group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.016))] p-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/[0.045] hover:shadow-[0_18px_48px_rgba(0,0,0,0.28)] md:border md:border-white/[0.08] md:p-4 md:hover:border-violet-400/30"
      onClick={() => {
        if (!opportunityId) return;
        onCardClick?.(opportunityId, opp);
      }}
      data-testid={dataTestId || (opportunityId ? `opportunity-card-${opportunityId}` : "opportunity-card")}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/40 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">{opp.niche}</p>
        <span className={`inline-flex items-center gap-1 rounded-full bg-black/20 px-2 py-0.5 text-[10px] font-medium md:border md:border-white/[0.07] ${heat.text}`}>
          {heat.icon}
          {heat.label}
        </span>
      </div>
      <h3
        className="mb-1.5 line-clamp-2 text-sm font-semibold leading-snug text-zinc-100"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {title}
      </h3>
      <p className="mb-3 line-clamp-2 text-xs leading-relaxed text-zinc-500">{opp.short_description}</p>

      <div className="mb-2 grid grid-cols-[1fr_auto] items-end gap-3 tabular-nums">
        <span className="min-w-0 text-lg font-semibold tracking-tight text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {revenueMetric.value}
          <span className="ml-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-600">{revenueMetric.label}</span>
        </span>
        <span className={`rounded-full bg-black/20 px-2 py-1 text-xs font-semibold ${growthTone}`}>
          {formatGrowthDisplay(opp)}
        </span>
      </div>
      <p className="mb-2.5 truncate text-[11px] text-zinc-500">
        {(opp.competition || "Medium") + " competition"}
        {opp.time_to_mvp ? ` · ${opp.time_to_mvp}` : ""}
      </p>

      <div className="mt-auto flex items-center justify-between gap-2 pt-2.5 md:border-t md:border-white/[0.06] md:pt-3">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onWhyClick?.(e, opp, opportunityId);
          }}
          className="inline-flex items-center gap-1 rounded-full text-[11px] font-medium text-violet-300/95 hover:text-violet-200"
        >
          <Sparkles className="h-3 w-3 shrink-0 opacity-80" aria-hidden />
          Why this?
        </button>
        <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-zinc-500 transition-colors group-hover:text-zinc-300">
          View details
          <ArrowRight className="h-3 w-3 shrink-0 text-zinc-600 group-hover:text-violet-400/90" aria-hidden />
        </span>
      </div>
    </div>
  );
}
